// Feature 09: Spike Platform Distribution Chart

import { Box, Paper, Typography, LinearProgress, Chip, useTheme } from '@mui/material';
import { SpikePlatformChartProps } from '../../types/distributionSignal.types';

const PLATFORM_CONFIG: Record<string, { label: string; color: string }> = {
  DIRECT: { label: 'Direct', color: '#1976d2' },
  ORGANIC_SEARCH: { label: 'Organic Search', color: '#388e3c' },
  SOCIAL: { label: 'Social Media', color: '#d32f2f' },
  REFERRAL: { label: 'Referral', color: '#f57c00' },
  EMAIL: { label: 'Email', color: '#7b1fa2' },
  PAID_SEARCH: { label: 'Paid Search', color: '#0097a7' },
  OTHER: { label: 'Other', color: '#757575' },
};

const SpikePlatformChart: React.FC<SpikePlatformChartProps> = ({
  breakdown,
  totalSessions,
  spike,
}) => {
  const theme = useTheme();

  const sortedBreakdown = [...breakdown].sort((a, b) => b.sessions - a.sessions);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        GA4 Platform Breakdown
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Total sessions during spike: {totalSessions.toLocaleString()}
      </Typography>

      <Box sx={{ mt: 2 }}>
        {sortedBreakdown.map((platform) => {
          const config = PLATFORM_CONFIG[platform.platform];
          const percent = (platform.sessions / totalSessions) * 100;

          return (
            <Box key={platform.platform} sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {config.label}
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={`${platform.sessions.toLocaleString()} (${percent.toFixed(1)}%)`}
                    size="small"
                    variant="outlined"
                  />
                  {platform.trend === 'UP' && (
                    <Chip label="↑ Up" size="small" color="success" variant="filled" />
                  )}
                  {platform.trend === 'DOWN' && (
                    <Chip label="↓ Down" size="small" color="error" variant="filled" />
                  )}
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  backgroundColor: theme.palette.divider,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: config.color,
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box mt={2} p={1} bgcolor="info.lighter" borderRadius={1}>
        <Typography variant="caption" color="info.dark">
          <strong>Pearson Correlation:</strong> {spike.correlationWithGa4.toFixed(3)} • Spike
          correlated with GA4 traffic {spike.correlationWithGa4 > 0.5 ? 'strongly' : spike.correlationWithGa4 > 0.3 ? 'moderately' : 'weakly'}
        </Typography>
      </Box>
    </Paper>
  );
};

export default SpikePlatformChart;
